import ComposingState from '../cache/composing';
import CandidateCache from '../cache/candidates';
import { CandidatesKeyboard, isAccentKey, isSpecialKey } from '../utils/KeyUtils';
import { SPECIAL_KEYS } from '../utils/constants';

export type CandidateProvider = (input: string, maxResults?: number) => Promise<string[]>;

const EXCLUDED_CANDIDATE_INPUT_TYPES: HTMLInputElement['type'][] = ['email', 'tel', 'url',];

const candidatesModule = (candidateProvider: CandidateProvider) => (keyboard: CandidatesKeyboard) => {
  const enableCache = false;
  let currentHighlightPage = 0;
  let currentCandidates: string[] = [];
  let selectedCandidateIndex = -1;

  const setInputRange = (input: string) => {
    const { enableLayoutCandidates } = keyboard.getOptions();
    if (enableLayoutCandidates) {
      const composing = ComposingState.get();
      const start = input.lastIndexOf(composing);
      const end = start !== -1 ? start + composing.length : start;

      if (start !== -1) {
        keyboard.options.onSelectionChange?.([start, end]);
      }
    }
  };

  const onSelectCandidate = (button: string) => (selectedCandidate: string, _event: MouseEvent) => {
    flushCandidates();
    const input = keyboard.getInput();
    const newInput = input.slice(0, -button.length) + selectedCandidate;
    keyboard.setInput(newInput);
    keyboard.setCaretPosition(newInput.length);
    keyboard.options.onChange?.(newInput);
  };

  const refreshCandidates = () => {
    const composing = ComposingState.get();
    if (composing) {
      updateCandidates(composing);
      setInputRange(keyboard.getInput());
    }
  };

  const flushCandidates = () => {
    const hadCandidates = currentCandidates.length > 0;
    ComposingState.reset();
    currentCandidates = [];
    selectedCandidateIndex = -1;
    currentHighlightPage = 0;
    setInputRange(keyboard.getInput());
    keyboard.setOptions({ layoutCandidates: {} });
    keyboard.candidateBox.destroy();
    if (hadCandidates) {
      keyboard.onCandidatesChange?.(false);
    }
  };

  const updateCandidates = async (button: string) => {
    const { enableLayoutCandidates } = keyboard.getOptions();
    if (button && enableLayoutCandidates) {
      let candidates = CandidateCache.get(button);
      if (!enableCache || !candidates || isAccentKey(button) ) {
        // Fetch new candidates from provider if composing state it's not in cache
        const newCandidates = (await candidateProvider(button)) ?? [];
        if (newCandidates.length > 0) {
          CandidateCache.set(button, newCandidates);
          candidates = CandidateCache.get(button);
        }
      }

      // Set keyboard candidates + show candidates box
      const hadCandidates = currentCandidates.length > 0;
      currentCandidates = candidates ? candidates.split(' ') : [];
      selectedCandidateIndex = -1;
      currentHighlightPage = 0;
      keyboard.setOptions({ layoutCandidates: CandidateCache.getAll() });
      requestAnimationFrame(() => {
        keyboard.candidateBox.show({
          candidateValue: candidates,
          targetElement: keyboard.keyboardDOM,
          onSelect: onSelectCandidate(button),
        });
      });
      if (!hadCandidates && currentCandidates.length > 0) {
        keyboard.onCandidatesChange?.(true);
      }
    } else {
      // No input, clear candidates
      flushCandidates();
    }
  };


  const highlightCandidate = (index: number) => {
    if (!keyboard.candidateBox?.candidateBoxElement) {
      return;
    }

    const pageSize = keyboard.candidateBox.pageSize || currentCandidates.length;
    const targetPage = Math.floor(index / pageSize);

    if (currentHighlightPage < targetPage) {
      keyboard.candidateBox.candidateBoxElement
        ?.querySelector('.hg-candidate-box-next')?.dispatchEvent(new MouseEvent('click'));
      currentHighlightPage++;
    }

    const indexInPage = index % pageSize;
    const container = keyboard.candidateBox.candidateBoxElement;
    container?.querySelector('.hg-candidate-box-list-item.selected')?.classList.remove('selected');
    container?.querySelectorAll('.hg-candidate-box-list-item')[indexInPage]?.classList.add('selected');
  };

  const selectNextCandidate = (): string | null => {
    if (currentCandidates.length === 0) {
      return null;
    }
    if (selectedCandidateIndex < currentCandidates.length - 1) {
      selectedCandidateIndex++;
    }
    highlightCandidate(selectedCandidateIndex);
    return currentCandidates[selectedCandidateIndex];
  };

  const confirmSelectedCandidate = () => {
    if (selectedCandidateIndex < 0 || !currentCandidates[selectedCandidateIndex]) {
      return;
    }
    const selected = currentCandidates[selectedCandidateIndex];
    const composing = ComposingState.get();
    flushCandidates();
    const input = keyboard.getInput();
    const newInput = input.slice(0, -composing.length) + selected;
    keyboard.setInput(newInput);
    keyboard.setCaretPosition(newInput.length);
    keyboard.options.onChange?.(newInput);
  };

  // Provide candidate functions to keyboard for other modules
  keyboard.updateCandidates = updateCandidates;
  keyboard.flushCandidates = flushCandidates;
  keyboard.setInputRange = setInputRange;
  keyboard.refreshCandidates = refreshCandidates;
  keyboard.selectNextCandidate = selectNextCandidate;
  keyboard.confirmSelectedCandidate = confirmSelectedCandidate;

  keyboard.registerModule('candidates', (module: any) => {
    module.fn = {
      handleButtonClicked: keyboard.handleButtonClicked,
      clearInput: keyboard.clearInput,
    };

    // Disable candidates if input type is excluded
    const { type: inputType } = keyboard.getOptions();
    if (EXCLUDED_CANDIDATE_INPUT_TYPES.includes(inputType)) {
      keyboard.setOptions({
        enableLayoutCandidates: false,
      });
    }

    // Init default candidates to cache and reset composing state
    ComposingState.reset();
    keyboard.options.onSelectionChange?.([0, 0]);
    CandidateCache.setCandidates(keyboard.getOptions().layoutCandidates);

    keyboard.handleButtonClicked = (...args) => {
      const [button] = args;
      const { enableLayoutCandidates } = keyboard.getOptions();
      const composing = ComposingState.get();

      if (enableLayoutCandidates) {
        if (button === SPECIAL_KEYS.CONFIRM || button === SPECIAL_KEYS.SPACE) {
          flushCandidates();
        }

        if (button === SPECIAL_KEYS.BACKSPACE) {
          ComposingState.set(composing.slice(0, -1));
          setInputRange(keyboard.getInput().slice(0, -1));
          updateCandidates(ComposingState.get());
        }

        // If the button is not a special key, treat it as composing input
        if (!isSpecialKey(button)) {
          ComposingState.set(composing + button);
          updateCandidates(ComposingState.get());
          setInputRange(keyboard.getInput() + button);
        }
      }
      module.fn.handleButtonClicked.call(keyboard, button);

      // simple-keyboard's internal caret tracking doesn't reliably update
      // when handleButtonClicked is called from a module override.
      // Explicitly sync the caret to the end of the current input.
      keyboard.setCaretPosition(keyboard.getInput().length);
    };

    keyboard.clearInput = function () {
      keyboard.setInput('');
      flushCandidates();
    };
  });
};

export default candidatesModule;
